<template>
  <v-stepper v-model="step" :disable="loading">
    <v-stepper-header>

      <v-stepper-step :complete="!!profile" editable step="1">Your profile</v-stepper-step>

      <v-divider></v-divider>

        <v-stepper-step :complete="!!name" :editable="!!profile" step="2">Name and picture</v-stepper-step>

      <v-divider></v-divider>

      <v-stepper-step :complete="!!date" :editable="!!name && allUploaded" step="3">Due date</v-stepper-step>

      <v-divider></v-divider>

      <v-stepper-step :complete="step > 4" :editable="!!date" step="4">Access settings</v-stepper-step>
    </v-stepper-header>

    <v-stepper-items>
      <v-stepper-content step="1">
        <div id="firebaseui-auth-container" />
        <Profile :profile="profile" icon="power_settings_new" @logout="logout" />
        <v-divider class="mt-4 mb-2"/>
        <v-btn
          color="primary"
          :disabled="(!profile || !profile.email || !profile.name)"
          @click="step += 1"
        >
          Continue
        </v-btn>
      </v-stepper-content>
      <v-stepper-content step="2">
        <v-layout wrap>
            <v-spacer />
            <v-flex xs12 md5>
                <v-subheader><kbd class="mx-1">Required</kbd> Enter a name for your pronostic</v-subheader>
                <v-text-field
                    label="Public name"
                    :rules="[value => !!name || 'Required.']"
                    v-model="name"
                    :placeholder="`${!profile || profile.isAnonymous ? 'My' : profile.name + '\'s' } baby`"
                    outline
                ></v-text-field>
            </v-flex>
            <v-spacer />
            <v-divider vertical />
            <v-spacer />
            <v-flex xs12 md5>
                <v-subheader><kbd class="mx-1">Optional</kbd> Select one or more images and click on <v-icon class="mx-1">cloud_upload</v-icon> to upload</v-subheader>
                <UploadFile 
                    :files="files"
                    @fileAdded="addFile"
                    @fileRemoved="removeFile"
                    @fileUploaded="fileUploaded"
                    ref="upload"/>
                <v-btn color="secondary" @click.native="select">Select image</v-btn>
            </v-flex>
            <v-spacer />
        </v-layout>
        <v-divider class="mt-4 mb-2"/>
        <v-btn
          color="primary"
          :disabled="(!name || !allUploaded)"
          @click="step += 1"
        >
          Continue
        </v-btn>
        <v-btn flat @click="step -= 1">Back</v-btn>
        <v-subheader v-if="!allUploaded">All images must be uploaded to continue</v-subheader>
      </v-stepper-content>
      <v-stepper-content step="3">
          <v-layout justify-center>
            <v-spacer />
            <v-flex xs12 sm8 md6 lg5 xl4>
                <v-date-picker
                    v-model="date"
                    :landscape="$vuetify.breakpoint.mdAndUp"
                    full-width
                    class="m-4 elevation-10"
                    ></v-date-picker>
            </v-flex>
            <v-spacer />
          </v-layout>
        <v-divider class="mt-4 mb-2"/>
        <v-btn
          color="primary"
          :disabled="!date"
          @click="step += 1"
        >
          Continue
        </v-btn>
        <v-btn flat @click="step -= 1">Back</v-btn>
      </v-stepper-content>

      <v-stepper-content step="4">
        <v-layout wrap>
            <v-flex xs6>
                <v-card-text>
                    <v-radio-group v-model="privacy">
                        <v-radio
                            v-for="p in privacies"
                            :key="p"
                            :label="privacy_text(p)"
                            :value="p"
                        ></v-radio>
                    </v-radio-group>
                </v-card-text>
            </v-flex>
            <v-flex xs6>
                    <v-alert
                        :value="true"
                        :type="privacy_color"
                        >
                       {{ privacy_description }}
                    </v-alert>
            </v-flex>
        </v-layout>
        <v-divider class="mt-4 mb-2"/>
        <v-btn
          color="primary"
          :disabled="!privacy"
          :loading="loading"
          @click="save"
        >
          Finish
        </v-btn>

        <v-btn flat @click="step -= 1">Back</v-btn>
      </v-stepper-content>
    </v-stepper-items>
  </v-stepper>
</template>

<script>
import { firestore, ui, uiConfig, auth } from "@/firebase";
import Profile from "@/components/user/Profile";
import UploadFile from "@/components/upload/UploadFile";
import UploadPreview from "@/components/upload/UploadPreview";

export default {
  data() {
    return {
      step: 1,
      date: null,
      name: null,
      picture: null,
      privacy: "private",
      privacies: ["public", "not_referenced", "private"],
      profile: null,
      files: [],
      states: [],
      loading: false
    };
  },
  mounted() {
    // The start method will wait until the DOM is loaded.
    if (auth.currentUser) {
      this.get_profile(auth.currentUser);
    } else {
      this.start_ui();
    }
  },
  methods: {
    async save() {
      this.loading = true;
      const doc = await firestore.collection("babies").add({
        name: this.name,
        pictures: this.files.map(file => file.ref),
        parent: auth.currentUser.uid,
        privacy: this.privacy,
        date: new Date(this.date)
      });
      this.$emit("created", doc);
      this.loading = false;
    },
    async logout() {
      await auth.signOut();
      this.profile = null;
      this.start_ui();
    },
    start_ui() {
      ui.start("#firebaseui-auth-container", {
        ...uiConfig,
        callbacks: {
          signInSuccessWithAuthResult: res => {
            this.get_profile(res.user);
          }
        }
      });
    },
    get_profile(user) {
      this.profile = {
        name: user.isAnonymous ? "Guest" : user.displayName,
        picture: user.photoURL,
        anonymous: user.isAnonymous,
        email: user.isAnonymous ? "anonymous" : user.email,
        id: user.uid
      };
    },
    select() {
      this.$refs.upload.inputFile();
    },
    uploadAll() {
      this.$refs.upload.uploadAll();
    },
    addFile(file) {
      if (this.files.indexOf(file) === -1) {
        this.files.push(file);
        this.states.push({ uploaded: false });
      }
    },
    removeFile(file) {
      const index = this.files.indexOf(file);
      if (index > -1) {
        this.files.splice(index, 1);
        this.states.splice(index, 1);
      }
    },
    fileUploaded(file) {
      const index = this.files.indexOf(file);
      if (index > -1) {
        this.states[index].uploaded = true;
      }
    },
    reinitialize() {
      this.files = [];
      this.collection = null;
      this.step = 1;
      this.$refs.upload.$refs.input.value = "";
    }
  },
  computed: {
    privacy_text() {
      return privacy => {
        switch (privacy) {
          case "public":
            return "public";
          case "not_referenced":
            return "not referenced";
          case "private":
            return "private";
        }
      };
    },
    privacy_description() {
      switch (this.privacy) {
        case "public":
          return "Anyone can view it.";
        case "not_referenced":
          return "Only those who know the link can view it, and you can share the link with anyone.";
        case "private":
          return "Only those you invite can view it.";
      }
    },
    privacy_color() {
      switch (this.privacy) {
        case "public":
          return "success";
        case "not_referenced":
          return "info";
        case "private":
          return "error";
      }
    },
    allUploaded() {
      return this.states.every(state => state.uploaded);
    }
  },
  components: {
    Profile,
    UploadFile,
    UploadPreview
  }
};
</script>
